
import { renderToStaticMarkup } from "react-dom/server";
import PartLabelTemplate, { type PartLabelTemplateProps } from "../components/stock/PartLabelTemplate";
import { defaultPartLabelDisplay, type PartLabelDisplay } from "../models/LabelDisplay";

import WarehouseLabelTemplate from "../components/stock/WarehouseLabelTemplate";
import {
  defaultWarehouseLabelDisplay,
  type WarehouseLabelDisplay
} from "../models/WarehouseLabelDisplay";
import VehicleLabelTemplate from "../components/vehicles/VehicleLabelTemplate";
import {
  defaultVehicleLabelDisplay,
  type VehicleLabelDisplay
} from "../models/VehicleLabelDisplay";

import { useQueryClient } from "@tanstack/react-query";
import { IWarehouseLocation } from "../models/Location";
import { IPart } from '../models/Part';
import useCustomerDetails from "../data/useCustomerDetails";
import {
  partLabelDisplayQueryKey,
  partLabelDisplayStaleTimeMs,
} from "../data/usePartLabels";
import {
  warehouseLabelDisplayQueryKey,
  warehouseLabelDisplayStaleTimeMs,
} from "../data/useWarehouseLabels";
import {
  vehicleLabelDisplayQueryKey,
  vehicleLabelDisplayStaleTimeMs,
} from "../data/useVehicleLabels";
import React from "react";
import useApiHelper from "./useApiHelper";
import { DefaultResponseWithData } from "../models/system";
import PartLabelCoreTemplate from "../components/stock/PartLabelCoreTemplate";
import { IVehicle } from "../models/Vehicle";

const RS_CORE_LOGO_SRC =
  "https://sawilmasharedstoragepuks.blob.core.windows.net/public/images/logos/rs/rs_core_logo.svg";

const toDataURL = async (url: string) => {
  const res = await fetch(url);
  const blob = await res.blob();

  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onloadend = () => resolve(reader.result)
    reader.onerror = reject
    reader.readAsDataURL(blob)
  });
}

export function useLabelGenerator() {
  const queryClient = useQueryClient();
  const { brand } = useCustomerDetails();
  const { get } = useApiHelper();

  const generatePartLabel = async (
    part: IPart
  ) => {
    const displayData = await queryClient.fetchQuery({
      queryKey: partLabelDisplayQueryKey,
      queryFn: () =>
        get<DefaultResponseWithData<PartLabelDisplay>>(`/template/label/display`),
      staleTime: partLabelDisplayStaleTimeMs,
    });

    const display = displayData.data ?? defaultPartLabelDisplay;

    const qrDataUrl = await toDataURL(
      `https://api.qrserver.com/v1/create-qr-code/?size=500x500&data=${part.id}`
    );

    const priceNumber = part.price ?? part.enteredPrice;
    const price = priceNumber ? `£${priceNumber.toFixed(2)}` : "£0.00";

    const html =
    "<!doctype html>" +
    renderToStaticMarkup(
      React.createElement(part.isCore ? PartLabelCoreTemplate : PartLabelTemplate, {
        display,
        logoSrc: brand?.data?.data.logo ?? "",
        secondLogoSrc: part.isCore ? RS_CORE_LOGO_SRC : undefined,
        qrSrc: qrDataUrl as string,
        tagNo: part.tagNumber ?? "No Tag No",
        date: part.created
          ? new Date(part.created).toLocaleDateString().replaceAll("/", " / ")
          : "",
        make: part.vehicle?.combinedMake ?? "",
        model: part.vehicle?.combinedModel ?? "",
        category: part.category?.name ?? "",
        vrm: part.vehicle?.vrm ?? "",
        year: part.vehicle?.dvlaYearOfManufacture?.toString?.() ?? "",
        engineCode: part.vehicle?.engineModelCode ?? "",
        vin: part.vehicle?.vinVerified ?? "",
        fuelType: part.vehicle?.fuelType ?? "",
        price,
        position: part.warehouseLocation?.displayName ?? "-",
        qc: part.passedQcOn != null ? "QC" : "-",
        imaged: part.images?.length > 0 ? "Img" : "-"
      })
    );

    return html;
  };

  const generateWarehouseLabel = async (
    warehouse: IWarehouseLocation
  ) => {
    const displayData = await queryClient.fetchQuery({
      queryKey: warehouseLabelDisplayQueryKey,
      queryFn: () =>
        get<DefaultResponseWithData<WarehouseLabelDisplay>>(
          `/template/warehouse/label/display`
        ),
      staleTime: warehouseLabelDisplayStaleTimeMs,
    });

    const display = displayData.data ?? defaultWarehouseLabelDisplay;

    const qrDataUrl = await toDataURL(
      `https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=${warehouse.id}`
    );

    const title = warehouse.displayName ?? "No Display Name";

    const html =
      "<!doctype html>" +
      renderToStaticMarkup(
        React.createElement(WarehouseLabelTemplate, {
          display,
          qrSrc: qrDataUrl as string,
          title,
          subtitle: "" // or warehouse.something later
        })
      );

    return html;
  };

  const generateVehicleLabel = async (
    vehicle: IVehicle
  ) => {
    const displayData = await queryClient.fetchQuery({
      queryKey: vehicleLabelDisplayQueryKey,
      queryFn: () =>
        get<DefaultResponseWithData<VehicleLabelDisplay>>(`/template/vehicle/label/display`),
      staleTime: vehicleLabelDisplayStaleTimeMs,
    });

    const display = displayData.data ?? defaultVehicleLabelDisplay;

    const qrDataUrl = display.qr
      ? await toDataURL(
        `https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=${vehicle.id}`
      )
      : undefined;

    const makeModel = `${vehicle.combinedMake ?? ""} ${vehicle.combinedModel ?? ""}`.trim();
    const mileage =
      vehicle.odometerInMiles != null ? `${vehicle.odometerInMiles.toLocaleString()} mi` : "-";

    const html =
      "<!doctype html>" +
      renderToStaticMarkup(
        React.createElement(VehicleLabelTemplate, {
          display,
          logoSrc: brand?.data?.data.logo ?? "",
          qrSrc: qrDataUrl as string | undefined,
          vrm: vehicle.vrm ?? "-",
          makeModel: makeModel || "-",
          stockNumber: vehicle.stockNumber ?? "-",
          vin: vehicle.vinVerified || vehicle.combinedVIN || "-",
          engineCode: vehicle.engineModelCode || "-",
          fuelType: vehicle.fuelType || "-",
          colour: vehicle.colourCurrent || "-",
          mileage,
        })
      );

    return html;
  };


  return { generatePartLabel, generateWarehouseLabel, generateVehicleLabel };
}